import { TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { InputBaseProps } from "@mui/material/InputBase";
import { getFieldSx } from "../../util/style";
import { formatDateInput } from "../../util/dateUtil";
import { Tooltip } from "./Tooltip";

type InputFieldProps = {
    className?: string;
    label: string;
    name: string;
    inputValue?: string;
    defaultValue?: string;
    handleValueChange?: (value: any, field?: string) => void,
    handleValidation?: (value: any, field?: string) => void | boolean,
    error?: string | undefined;
    type?: "text" | "date" | "oib";
    maxLength?: number;
    multiline?: boolean;
    disabled?: boolean;
    placeholder?: string;
    info?: string;
}

export default function InputField({
                                       className,
                                       label,
                                       name,
                                       inputValue,
                                       defaultValue,
                                       handleValueChange = () => {
                                       },
                                       error,
                                       handleValidation = () => {
                                       },
                                       type = "text",
                                       maxLength,
                                       multiline = false,
                                       disabled = false,
                                       placeholder = "",
                                       info,
                                   }: InputFieldProps) {
    const [value, setValue] = useState<string | undefined>((defaultValue === "") ? undefined : defaultValue);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const isDateType = type === "date";
    const isOibType = type === "oib";


    const handleInputProps = () => {
        let inputProps: InputBaseProps['inputProps'] = {} as InputBaseProps['inputProps'];

        if (!!maxLength) {
            inputProps = {
                maxLength: maxLength
            }
        }

        if (type === "date") {
            inputProps = {
                ...inputProps,
                maxLength: 10,
                inputMode: 'numeric',
                pattern: "\\d{2}\\.\\d{2}\\.\\d{4}",
            }
        }

        return inputProps;
    }

    const onBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!value) {
            handleValidation(event.target.value, name);
        }
        setIsFocused(false);
    }

    const onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (isDateType) {
            setValue(formatDateInput(event.target.value));
        } else if (isOibType) {
            if (event.target.value.length <= 11) {
                handleValidation(event.target.value);
                setValue(event.target.value)
            }
        } else {
            setValue(event.target.value);
        }
    }

    useEffect(() => {
        if (value !== undefined) {
            const handler = setTimeout(() => {
                handleValueChange(value, name);
                if (isFocused) {
                    handleValidation(value, name);
                }
            }, 200)

            return () => {
                clearTimeout(handler);
            };
        }
    }, [value]);

    useEffect(() => {
        if (inputValue !== undefined) {
            setValue(inputValue);
        }
    }, [inputValue])

    return (
      <div className={className}>
          <TextField
            multiline={multiline || disabled}
            required
            fullWidth
            placeholder={placeholder}
            disabled={disabled}
            variant="outlined"
            label={label}
            value={value ?? ""}
            onChange={onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={onBlur}
            name={name}
            sx={getFieldSx(name, error)}
            error={!!error}
            helperText={error}
            InputLabelProps={{
                sx: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: !!info ? 'calc(100% - 50px)' : 'calc(85%)',

                    '&.MuiInputLabel-shrink': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '120%',
                    },
                },
                required: false
            }}
            inputProps={handleInputProps}
            slotProps={
                {
                    input: {
                        endAdornment:
                          <>
                              {info && <Tooltip text={info} />}
                          </>
                    }
                }}
          />
      </div>
    )
}
